import {
  Add as AddIcon,
  AssignmentTurnedIn as TaskIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  ExpandMore as ExpandMoreIcon,
  Group as GroupIcon,
  Language as LanguageIcon,
  LinkOff as LinkOffIcon,
  LocalShipping as LocalShippingIcon,
  Login as LoginIcon,
  NoteAlt as NoteIcon,
  OpenInNew as OpenInNewIcon,
  Person as PersonIcon,
  PersonAddAlt1 as PersonAddAlt1Icon,
  PersonRemove as PersonRemoveIcon,
  Phone as PhoneIcon,
  Place as PlaceIcon,
  PostAdd as PostAddIcon,
  Schedule as ScheduleIcon,
  Search as SearchIcon,
  Sms as SmsIcon,
  Warehouse as WarehouseIcon,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  alpha,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from '@mui/lab';
import type { Contact } from '@otw/models/leads/contacts';
import type {
  ContactCompany,
  ContactCompanyActivityType,
  ContactCompanyMember,
  ContactCompanyRole,
} from '@otw/models/leads/contactCompanies';
import type { Lead } from '@otw/models/leads/lead';
import type { InventoryLead } from '@otw/models/leads/inventoryLead';
import React, { useMemo, useState } from 'react';
import { usePickupLocations } from 'hooks/inventory/usePickupLocations';
import { useTerminals } from 'hooks/terminals/useTerminals';
import type { Terminal } from '@otw/models/leads/terminals';
import { getTerminalDisplayName } from '@otw/models/leads/terminals';
import { resolveTerminalLocation } from '@/core/terminals/terminalAddress';
import { createTerminalFilterOptions } from '@/core/terminals/terminalSearch';
import { getCompanyTerminals } from 'views/SuperAdmin/Management/TerminalManagement/util/terminalCompanyLinks';
import { openTerminalDetailInNewTab } from 'views/SuperAdmin/Management/TerminalManagement/util/terminalRoutes';
import TerminalFormDialog from 'views/SuperAdmin/Management/TerminalManagement/components/TerminalFormDialog';
import {
  formatPickupLocationAddress,
  formatPickupLocationAddressesByIds,
  resolvePickupLocationById,
} from '../util/pickupLocationDisplay';
import { previewAccordionSummarySx } from '../util/previewAccordionSummarySx';
import { previewPaneBodySx, previewPanePaperSx } from '../util/previewPanePaperSx';
import { useSnackbar } from '@/hooks/common/useSnackbar';
import { useInventoryLeads } from 'hooks/admin/useInventoryLeads';
import { useLeads } from 'hooks/admin/useLeads';
import { useContactCompanies } from 'hooks/contacts/useContactCompanies';
import { getContactPrimaryCompanyName } from '../util/contactCompanyMembership';
import { useContacts } from 'hooks/contacts/useContacts';
import { useTasks } from 'hooks/tasks/useTasks';
import { getUserDisplayName, useSalesAssignableUsers, useUsers } from 'hooks/useUsers';
import { usePermissions } from 'hooks/auth/usePermissions';
import {
  ActivityTimelineEntryDate,
  ActivityTimelineEntryTitle,
} from './ActivityTimelineEntry';
import { activityAuthorDisplayName } from '../util/activityDisplay';
import { openCompanyDetailInNewTab } from '../util/companyRoutes';
import { contactToTaskCustomerOption } from '../util/contactLeadSync';
import { logTaskCreationContactActivity } from '../util/contactTaskActivity';
import { resolveOwnerDisplayName } from '../util/resolveOwnerDisplayName';
import {
  ContactCompanyActivityLogDialog,
  ContactFormDialog,
  InventoryLeadFormDialog,
  LeadFormDialog,
  SelectContactForLeadDialog,
  TaskFormDialog,
} from './dialogs';
import type {
  CompanyMemberForLead,
  ContactCompanyActivityLogSubmitPayload,
  InitialClientForLead,
  LoggableActivityType,
  TaskAssignableUser,
  TaskFormSubmitPayload,
} from './dialogs';
import type { TaskCustomerOption } from '../util/taskUtils';
import {
  buildCompanyTimelineEntries,
  companyTimelineEntryAuthorId,
  filterCompanyTimelineEntries,
  getCompanyTimelineDisplay,
  getCompanyTimelineDomain,
  type CompanyActivityFilter,
  type CompanyTimelineEntry,
} from '../util/companyTimeline';
import type { CrmActivityDomainFilter } from '../util/contactMergedTimeline';
import { activityDomainPaperSx } from '../util/activityDomainPaperSx';
import { ActivityDomainLabel } from './ActivityDomainLabel';

interface ContactCompanyPreviewPaneProps {
  company: ContactCompany;
  onClose: () => void;
  onEdit?: (company: ContactCompany) => void;
  /** Optional callback to open the preview pane for a specific contact instead. */
  onSelectContact?: (contact: Contact) => void;
}

const ROLE_LABEL: Record<ContactCompanyRole, string> = {
  owner: 'Owner',
  manager: 'Manager',
  partner: 'Partner',
};

const ROLE_OPTIONS: { value: ContactCompanyRole; label: string }[] = [
  { value: 'owner', label: 'Owner' },
  { value: 'manager', label: 'Manager' },
  { value: 'partner', label: 'Partner' },
];

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

const formatDate = (date: Date | null | undefined): string => {
  if (!date) return '—';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '—';
  return dateFormatter.format(d);
};

interface AboutRowProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}

const AboutRow: React.FC<AboutRowProps> = ({ icon, label, value }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start">
    <Box sx={{ color: 'text.secondary', mt: 0.25, display: 'flex' }}>{icon}</Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Box sx={{ mt: 0.25 }}>
        {typeof value === 'string' ? (
          <Typography variant="body2">{value || '—'}</Typography>
        ) : (
          value
        )}
      </Box>
    </Box>
  </Stack>
);

interface ActivityIconMeta {
  icon: React.ReactNode;
  color: (palette: Theme['palette']) => string;
}

const ACTIVITY_ICON_META: Record<ContactCompanyActivityType | 'created', ActivityIconMeta> = {
  created: {
    icon: <BusinessIcon fontSize="small" />,
    color: (p) => p.primary.main,
  },
  call: {
    icon: <PhoneIcon fontSize="small" />,
    color: (p) => p.success.main,
  },
  sms: {
    icon: <SmsIcon fontSize="small" />,
    color: (p) => p.info.main,
  },
  email: {
    icon: <EmailIcon fontSize="small" />,
    color: (p) => p.primary.main,
  },
  note: {
    icon: <NoteIcon fontSize="small" />,
    color: (p) => p.warning.main,
  },
  task: {
    icon: <TaskIcon fontSize="small" />,
    color: (p) => p.secondary.main,
  },
  lead: {
    icon: <PostAddIcon fontSize="small" />,
    color: (p) => p.primary.dark,
  },
  shipping_repairs: {
    icon: <LocalShippingIcon fontSize="small" />,
    color: (p) => p.secondary.main,
  },
};

type ActivityFilter = CompanyActivityFilter;

const ACTIVITY_FILTER_OPTIONS: { value: ActivityFilter; label: string }[] = [
  { value: 'all', label: 'All activity' },
  { value: 'created', label: 'Company created' },
  { value: 'call', label: 'Calls' },
  { value: 'sms', label: 'SMS' },
  { value: 'email', label: 'Emails' },
  { value: 'note', label: 'Notes' },
  { value: 'task', label: 'Tasks' },
  { value: 'lead', label: 'Leads' },
  { value: 'shipping_repairs', label: 'Shipping & Repairs' },
];

const DOMAIN_FILTER_OPTIONS: { value: CrmActivityDomainFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'sales', label: 'Sales' },
  { value: 'inventory', label: 'Inventory' },
  { value: 'marketing', label: 'Marketing' },
];

const ContactCompanyPreviewPane: React.FC<ContactCompanyPreviewPaneProps> = ({
  company,
  onClose,
  onEdit,
  onSelectContact,
}) => {
  const theme = useTheme();
  const { showError } = useSnackbar();
  const {
    companies: contactCompanies,
    logCompanyActivity,
    addCompanyMember,
    updateCompanyMemberRole,
    removeCompanyMember,
    addCompanyTerminal,
    removeCompanyTerminal,
    isMutatingMembers,
    isMutatingTerminals,
  } = useContactCompanies();
  const { contacts, logContactActivity, isLoggingActivity: isLoggingContactActivity } =
    useContacts();
  const { leads } = useLeads();
  const { inventoryLeads } = useInventoryLeads();
  const { createTask } = useTasks();
  const { users } = useUsers();
  const { leadManagementScope } = usePermissions();
  const hasFullLeadManagementAccess = leadManagementScope === 'all';
  const { users: salesAssignableUsers } = useSalesAssignableUsers();
  const { pickupLocations } = usePickupLocations();
  const { terminals } = useTerminals();
  const companyTerminals = useMemo(
    () => getCompanyTerminals(company, terminals),
    [company, terminals],
  );

  const addableTerminals = useMemo(() => {
    const linkedIds = new Set(company.terminalIds ?? []);
    return terminals.filter((terminal) => !linkedIds.has(terminal.id));
  }, [company.terminalIds, terminals]);

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [logType, setLogType] = useState<LoggableActivityType | null>(null);
  const [activitySearch, setActivitySearch] = useState('');
  const [activityFilter, setActivityFilter] = useState<ActivityFilter>('all');
  const [activityDomainFilter, setActivityDomainFilter] =
    useState<CrmActivityDomainFilter>('all');
  const [addContactValue, setAddContactValue] = useState<Contact | null>(null);
  const [addContactRole, setAddContactRole] = useState<ContactCompanyRole>('manager');
  const [addTerminalValue, setAddTerminalValue] = useState<Terminal | null>(null);
  const [terminalCreateOpen, setTerminalCreateOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [memberPickMode, setMemberPickMode] = useState<'lead' | 'inventory-lead' | 'task' | null>(
    null,
  );
  const [leadFormOpen, setLeadFormOpen] = useState(false);
  const [leadFormMountKey, setLeadFormMountKey] = useState(0);
  const [contactForNewLead, setContactForNewLead] = useState<Contact | null>(null);
  const [contactForNewInventoryLead, setContactForNewInventoryLead] = useState<Contact | null>(
    null,
  );
  const [inventoryLeadFormOpen, setInventoryLeadFormOpen] = useState(false);
  const [contactForTask, setContactForTask] = useState<Contact | null>(null);
  const [contactFormOpen, setContactFormOpen] = useState(false);

  const accent = theme.palette.primary.main;

  /** Hydrated members joined against the contact list. */
  const memberRows = useMemo(() => {
    const byId = new Map(contacts.map((c) => [c.id, c]));
    return company.members
      .map((member) => ({
        member,
        contact: byId.get(member.contactId) ?? null,
      }))
      .sort((a, b) => {
        const aName = a.contact?.name ?? '';
        const bName = b.contact?.name ?? '';
        return aName.localeCompare(bName);
      });
  }, [company.members, contacts]);

  /** Contacts available to add (not already a member of this company). */
  const addableContacts = useMemo(() => {
    const inMembers = new Set(company.members.map((m) => m.contactId));
    return contacts.filter((c) => !inMembers.has(c.id));
  }, [company.members, contacts]);

  /** Member-contact ids for quick lookup when merging timeline entries. */
  const memberContactById = useMemo(() => {
    const map = new Map<string, Contact>();
    for (const row of memberRows) {
      if (row.contact) map.set(row.contact.id, row.contact);
    }
    return map;
  }, [memberRows]);

  const contactsById = useMemo(
    () => new Map(contacts.map((c) => [c.id, c])),
    [contacts],
  );

  const memberContacts = useMemo(
    () => Array.from(memberContactById.values()),
    [memberContactById],
  );

  const timelineEntries = useMemo<CompanyTimelineEntry[]>(
    () =>
      buildCompanyTimelineEntries({
        company,
        memberContacts,
        leads: leads ?? [],
        inventoryLeads: inventoryLeads ?? [],
      }),
    [company, memberContacts, leads, inventoryLeads],
  );

  const visibleTimelineEntries = useMemo(
    () =>
      filterCompanyTimelineEntries(
        timelineEntries,
        activitySearch,
        activityFilter,
        activityDomainFilter,
      ),
    [timelineEntries, activitySearch, activityFilter, activityDomainFilter],
  );

  /** Contact chip shown on a timeline row, resolving company-level `lead` activities too. */
  const resolveTimelineContactChip = (entry: CompanyTimelineEntry): string | null => {
    if (entry.kind === 'contact-activity') {
      return entry.contact.name || null;
    }
    if (entry.kind === 'company-activity' && entry.activity.contactId) {
      const contact = contactsById.get(entry.activity.contactId);
      return contact?.name || null;
    }
    return null;
  };

  const openMenu = (event: React.MouseEvent<HTMLElement>) => setMenuAnchor(event.currentTarget);
  const closeMenu = () => setMenuAnchor(null);

  const handlePickLogActivity = (type: LoggableActivityType) => {
    closeMenu();
    setLogType(type);
  };

  const handleLogSubmit = async ({
    note,
    contactId,
    domain,
  }: ContactCompanyActivityLogSubmitPayload) => {
    if (!logType) return;
    if (!contactId) {
      showError('Select a contact for this activity.');
      return;
    }
    await logContactActivity({
      contactId,
      type: logType,
      note,
      domain,
    });
    setLogType(null);
  };

  const handlePickCreateTask = () => {
    closeMenu();
    setContactForTask(null);
    setMemberPickMode('task');
  };

  const handlePickCreateLead = () => {
    closeMenu();
    setContactForNewLead(null);
    setMemberPickMode('lead');
  };

  const handlePickCreateInventoryLead = () => {
    closeMenu();
    setContactForNewInventoryLead(null);
    setMemberPickMode('inventory-lead');
  };

  const handleMemberPickConfirmed = (contact: Contact) => {
    if (memberPickMode === 'lead') {
      handleContactForLeadConfirmed(contact);
    } else if (memberPickMode === 'inventory-lead') {
      setContactForNewInventoryLead(contact);
      setInventoryLeadFormOpen(true);
    } else if (memberPickMode === 'task') {
      setContactForTask(contact);
      setTaskDialogOpen(true);
    }
    setMemberPickMode(null);
  };

  const handleContactForLeadConfirmed = (contact: Contact) => {
    setContactForNewLead(contact);
    setLeadFormMountKey((k) => k + 1);
    setLeadFormOpen(true);
  };

  const handleInventoryLeadCreated = async (lead: InventoryLead) => {
    const truck = lead.trucks?.[0];
    const truckLabel = truck
      ? [truck.year, truck.make, truck.model].filter(Boolean).join(' ')
      : '';
    const who = [company.name?.trim(), contactForNewInventoryLead?.name?.trim()]
      .filter(Boolean)
      .join(' · ');
    const note = [who, truckLabel].filter(Boolean).join(' · ') || 'New inventory lead';
    await logCompanyActivity({
      companyId: company.id,
      type: 'lead',
      note,
      leadId: lead.id,
      domain: 'inventory',
      ...(contactForNewInventoryLead ? { contactId: contactForNewInventoryLead.id } : {}),
    });
  };

  /** Build the lead's `initialClient` from the chosen contact + this company. */
  const initialClientForNewLead = useMemo<InitialClientForLead | null>(() => {
    const c = contactForNewLead;
    if (!c?.userId?.trim()) return null;
    const displayName = c.name.trim();
    const parts = displayName.split(/\s+/).filter(Boolean);
    return {
      id: c.userId.trim(),
      first_name: parts[0] ?? '',
      last_name: parts.slice(1).join(' '),
      email: c.email ?? '',
      phone: c.phone,
      company_name: company.name,
      ...(displayName ? { displayName } : {}),
    };
  }, [contactForNewLead, company.name]);

  const taskCustomersForDialog = useMemo((): TaskCustomerOption[] => {
    if (!contactForTask) return [];
    return [contactToTaskCustomerOption(contactForTask, leads ?? [])];
  }, [contactForTask, leads]);

  const assignableUsers = useMemo<TaskAssignableUser[]>(() => {
    if (!hasFullLeadManagementAccess) return [];
    return salesAssignableUsers.map((user) => ({
      id: String(user.id),
      displayName: getUserDisplayName(user),
      email: user.email || null,
    }));
  }, [hasFullLeadManagementAccess, salesAssignableUsers]);

  const handleTaskSubmit = async (task: TaskFormSubmitPayload) => {
    const created = await createTask(task);
    await logTaskCreationContactActivity(task, created.id, logContactActivity);
    setTaskDialogOpen(false);
    setContactForTask(null);
  };

  const handleLeadCreatedFromCompany = async (lead: Lead) => {
    const note = [lead.company?.trim(), lead.customerName?.trim()]
      .filter(Boolean)
      .join(' · ') || 'New lead';
    await logCompanyActivity({
      companyId: company.id,
      type: 'lead',
      note,
      leadId: lead.id,
      domain: 'sales',
      ...(contactForNewLead ? { contactId: contactForNewLead.id } : {}),
    });
  };

  const handleAddContact = async () => {
    if (!addContactValue) return;
    await addCompanyMember({
      companyId: company.id,
      contactId: addContactValue.id,
      role: addContactRole,
    });
    setAddContactValue(null);
    setAddContactRole('manager');
  };

  const handleAddTerminal = async () => {
    if (!addTerminalValue) return;
    await addCompanyTerminal({
      companyId: company.id,
      terminalId: addTerminalValue.id,
    });
    setAddTerminalValue(null);
  };

  const handleRemoveTerminal = async (terminalId: string) => {
    await removeCompanyTerminal({
      companyId: company.id,
      terminalId,
    });
  };

  const handleChangeRole = async (member: ContactCompanyMember, role: ContactCompanyRole) => {
    if (member.role === role) return;
    await updateCompanyMemberRole({
      companyId: company.id,
      contactId: member.contactId,
      role,
    });
  };

  const handleRemoveMember = async (member: ContactCompanyMember) => {
    await removeCompanyMember({
      companyId: company.id,
      contactId: member.contactId,
    });
  };

  const businessAddressText = formatPickupLocationAddress(
    resolvePickupLocationById(pickupLocations, company.businessAddressId),
  );
  const dropoffAddressLabels = formatPickupLocationAddressesByIds(
    pickupLocations,
    company.dropoffLocationIds ?? [],
  );
  const dropoffAddressText = dropoffAddressLabels.join(' • ');

  const companyMembersForLead = useMemo<CompanyMemberForLead[]>(
    () =>
      memberRows.flatMap(({ member, contact }) =>
        contact ? [{ contact, role: member.role }] : [],
      ),
    [memberRows],
  );

  /** True if any member contact has a portal user account (required to create a lead). */
  const hasLeadEligibleContact = useMemo(
    () =>
      memberRows.some(({ contact }) => Boolean(contact?.userId?.trim())),
    [memberRows],
  );

  return (
    <Paper variant="outlined" sx={previewPanePaperSx}>
      {/* Header */}
      <Box
        sx={{
          p: 2.5,
          background: `linear-gradient(135deg, ${alpha(accent, 0.18)} 0%, ${alpha(accent, 0.04)} 100%)`,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ mb: 1.5 }}
        >
          <Chip
            size="small"
            icon={<BusinessIcon sx={{ fontSize: 14 }} />}
            label="Company"
            sx={{
              height: 22,
              fontSize: 12,
              fontWeight: 600,
              bgcolor: alpha(accent, 0.18),
              color: accent,
              '& .MuiChip-icon': { color: 'inherit', ml: 0.5 },
              '& .MuiChip-label': { px: 1 },
            }}
          />
          <Stack direction="row" spacing={0.5}>
            {onEdit && (
              <Tooltip title="Edit company">
                <IconButton size="small" onClick={() => onEdit(company)} aria-label="Edit company">
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Open in new tab">
              <IconButton
                size="small"
                onClick={() => openCompanyDetailInNewTab(company.id)}
                aria-label="Open company in new tab"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={onClose} aria-label="Close preview">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            variant="rounded"
            sx={{
              width: 64,
              height: 64,
              bgcolor: alpha(accent, 0.22),
              color: accent,
              fontSize: 22,
              fontWeight: 800,
            }}
          >
            {company.name?.charAt(0)?.toUpperCase() || '?'}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }} noWrap>
              {company.name || 'Unnamed company'}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {memberRows.length} {memberRows.length === 1 ? 'contact' : 'contacts'} •{' '}
              {resolveOwnerDisplayName(company.ownerId, users)}
            </Typography>
            {company.website ? (
              <Link
                href={
                  company.website.startsWith('http')
                    ? company.website
                    : `https://${company.website}`
                }
                target="_blank"
                rel="noreferrer"
                variant="body2"
                color="primary"
                underline="hover"
                sx={{
                  display: 'block',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {company.website}
              </Link>
            ) : null}
          </Box>
        </Stack>
      </Box>

      {/* Body */}
      <Box sx={previewPaneBodySx}>
        {/* About */}
        <Accordion
          defaultExpanded
          disableGutters
          elevation={0}
          sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent' }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={previewAccordionSummarySx}>
            <Typography fontWeight={700}>About</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1.5}>
              <AboutRow
                icon={<LanguageIcon fontSize="small" />}
                label="Website"
                value={
                  company.website ? (
                    <Link
                      href={
                        company.website.startsWith('http')
                          ? company.website
                          : `https://${company.website}`
                      }
                      target="_blank"
                      rel="noreferrer"
                      variant="body2"
                      color="primary"
                      underline="hover"
                    >
                      {company.website}
                    </Link>
                  ) : (
                    '—'
                  )
                }
              />
              <AboutRow
                icon={<PersonIcon fontSize="small" />}
                label="Company owner"
                value={resolveOwnerDisplayName(company.ownerId, users)}
              />
              <AboutRow
                icon={<PlaceIcon fontSize="small" />}
                label="Business address"
                value={businessAddressText || '—'}
              />
              <AboutRow
                icon={<LocalShippingIcon fontSize="small" />}
                label="Dropoff address"
                value={dropoffAddressText || '—'}
              />
              <AboutRow
                icon={<ScheduleIcon fontSize="small" />}
                label="Last activity"
                value={formatDate(company.lastActivityAt)}
              />
              {company.notes ? (
                <AboutRow
                  icon={<NoteIcon fontSize="small" />}
                  label="Notes"
                  value={
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {company.notes}
                    </Typography>
                  }
                />
              ) : null}
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Divider sx={{ my: 0.5 }} />

        {/* Terminals */}
        <Accordion
          defaultExpanded
          disableGutters
          elevation={0}
          sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent' }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={previewAccordionSummarySx}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: '100%' }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography fontWeight={700}>Terminals</Typography>
                <Chip
                  size="small"
                  label={companyTerminals.length}
                  sx={{
                    height: 20,
                    fontSize: 11,
                    fontWeight: 700,
                    bgcolor: alpha(theme.palette.info.main, 0.12),
                    color: theme.palette.info.dark,
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              </Stack>
              <Button
                size="small"
                variant="outlined"
                startIcon={<WarehouseIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  setTerminalCreateOpen(true);
                }}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, mr: 0.5 }}
              >
                Create terminal
              </Button>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack
              direction="row"
              spacing={1}
              alignItems="stretch"
              sx={{ mb: 1.5 }}
            >
              <Autocomplete
                size="small"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  '& .MuiOutlinedInput-root': { height: '100%', borderRadius: 2 },
                }}
                options={addableTerminals}
                value={addTerminalValue}
                onChange={(_, value) => setAddTerminalValue(value)}
                disabled={isMutatingTerminals}
                filterOptions={createTerminalFilterOptions(pickupLocations)}
                getOptionLabel={(option) =>
                  getTerminalDisplayName(
                    option,
                    resolveTerminalLocation(option, pickupLocations),
                  )
                }
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                renderOption={(props, option) => {
                  const location = resolveTerminalLocation(option, pickupLocations);
                  return (
                    <li {...props} key={option.id}>
                      <Stack>
                        <Typography variant="body2" fontWeight={600}>
                          {getTerminalDisplayName(option, location)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatPickupLocationAddress(location) || 'No address'}
                        </Typography>
                      </Stack>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search by name, number, or address"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <WarehouseIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      sx: { borderRadius: 2, bgcolor: 'background.paper' },
                    }}
                  />
                )}
              />
              <Button
                size="small"
                variant="contained"
                disableElevation
                startIcon={<AddIcon />}
                onClick={() => void handleAddTerminal()}
                disabled={!addTerminalValue || isMutatingTerminals}
                sx={{
                  flexShrink: 0,
                  px: 1.5,
                  fontSize: 14,
                  lineHeight: 1.25,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                Add
              </Button>
            </Stack>
            {companyTerminals.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                No terminals linked yet. Search above or create one.
              </Alert>
            ) : (
              <Stack spacing={1}>
                {companyTerminals.map((terminal) => {
                  const location = resolveTerminalLocation(terminal, pickupLocations);
                  return (
                    <Paper
                      key={terminal.id}
                      variant="outlined"
                      sx={{
                        p: 1.25,
                        borderRadius: 2,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.04) },
                      }}
                      onClick={() => openTerminalDetailInNewTab(terminal.id)}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <WarehouseIcon fontSize="small" sx={{ color: theme.palette.info.main }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={700} noWrap>
                            {getTerminalDisplayName(terminal, location)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {formatPickupLocationAddress(location) || 'No address'}
                          </Typography>
                        </Box>
                        <Tooltip title="Remove from this company">
                          <span>
                            <IconButton
                              size="small"
                              aria-label="Remove terminal from company"
                              disabled={isMutatingTerminals}
                              onClick={(event) => {
                                event.stopPropagation();
                                void handleRemoveTerminal(terminal.id);
                              }}
                            >
                              <LinkOffIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <OpenInNewIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </AccordionDetails>
        </Accordion>

        <Divider sx={{ my: 0.5 }} />

        {/* Recent activities */}
        <Accordion
          defaultExpanded
          disableGutters
          elevation={0}
          sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent' }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={previewAccordionSummarySx}>
            <Typography fontWeight={700}>Recent activities</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack
              direction="row"
              spacing={1}
              alignItems="stretch"
              sx={{ mb: 1.5 }}
            >
              <TextField
                size="small"
                value={activitySearch}
                onChange={(e) => setActivitySearch(e.target.value)}
                placeholder="Search"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  '& .MuiOutlinedInput-root': { height: '100%' },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 2, bgcolor: 'background.paper' },
                }}
              />
              <TextField
                select
                size="small"
                value={activityDomainFilter}
                onChange={(e) =>
                  setActivityDomainFilter(e.target.value as CrmActivityDomainFilter)
                }
                sx={{
                  flexShrink: 0,
                  minWidth: 100,
                  '& .MuiOutlinedInput-root': { height: '100%' },
                  '& .MuiSelect-select': { py: 1, display: 'flex', alignItems: 'center' },
                }}
                InputProps={{ sx: { borderRadius: 2, bgcolor: 'background.paper' } }}
              >
                {DOMAIN_FILTER_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 14 }}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                value={activityFilter}
                onChange={(e) => setActivityFilter(e.target.value as ActivityFilter)}
                sx={{
                  flexShrink: 0,
                  minWidth: 110,
                  '& .MuiOutlinedInput-root': { height: '100%' },
                  '& .MuiSelect-select': { py: 1, display: 'flex', alignItems: 'center' },
                }}
                InputProps={{ sx: { borderRadius: 2, bgcolor: 'background.paper' } }}
              >
                {ACTIVITY_FILTER_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 14 }}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                size="small"
                variant="contained"
                disableElevation
                startIcon={<AddIcon />}
                onClick={openMenu}
                sx={{
                  flexShrink: 0,
                  px: 1.5,
                  fontSize: 14,
                  lineHeight: 1.25,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                Add
              </Button>
            </Stack>

            <Box
              sx={{
                maxHeight: '50vh',
                overflowY: 'auto',
                pr: 0.25,
                scrollbarGutter: 'stable',
              }}
            >
              {visibleTimelineEntries.length === 0 ? (
                <Box
                  sx={{
                    py: 3,
                    textAlign: 'center',
                    color: 'text.secondary',
                    border: 1,
                    borderStyle: 'dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="body2">No activities match your filters.</Typography>
                </Box>
              ) : (
                <Timeline
                  sx={{
                    m: 0,
                    p: 0,
                    '& .MuiTimelineItem-root:before': { display: 'none', flex: 0, padding: 0 },
                  }}
                >
                  {visibleTimelineEntries.map((entry, index) => {
                    const isLast = index === visibleTimelineEntries.length - 1;
                    const display = getCompanyTimelineDisplay(entry);
                    const attribution = resolveTimelineContactChip(entry);
                    const meta = ACTIVITY_ICON_META[display.activityType];
                    const iconColor = meta.color(theme.palette);
                    const authorName = activityAuthorDisplayName(
                      users,
                      companyTimelineEntryAuthorId(entry),
                    );
                    const domain = getCompanyTimelineDomain(entry);
                    return (
                      <TimelineItem key={entry.id}>
                        <TimelineSeparator>
                          <TimelineDot
                            sx={{
                              bgcolor: alpha(iconColor, 0.16),
                              color: iconColor,
                              boxShadow: 'none',
                              m: 0,
                              width: 28,
                              height: 28,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {meta.icon}
                          </TimelineDot>
                          {!isLast && <TimelineConnector sx={{ bgcolor: 'divider' }} />}
                        </TimelineSeparator>
                        <TimelineContent sx={{ pb: 1.5, pr: 0 }}>
                          <Paper
                            variant="outlined"
                            sx={activityDomainPaperSx(domain)}
                            title={
                              domain === 'inventory'
                                ? 'Inventory activity'
                                : domain === 'sales'
                                  ? 'Sales activity'
                                  : undefined
                            }
                          >
                            <Box sx={{ p: 1.5 }}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                flexWrap="wrap"
                                useFlexGap
                                sx={{ rowGap: 0.5 }}
                              >
                                <ActivityTimelineEntryTitle
                                  title={display.title}
                                  authorName={authorName}
                                  variant="body2"
                                />
                                {attribution ? (
                                  <Chip
                                    size="small"
                                    icon={<PersonIcon sx={{ fontSize: 14 }} />}
                                    label={attribution}
                                    sx={{
                                      height: 20,
                                      fontSize: 11,
                                      fontWeight: 600,
                                      bgcolor: alpha(theme.palette.secondary.main, 0.12),
                                      color: theme.palette.secondary.dark,
                                      '& .MuiChip-icon': { color: 'inherit', ml: 0.5 },
                                      '& .MuiChip-label': { px: 0.75 },
                                    }}
                                  />
                                ) : null}
                              </Stack>
                              <ActivityTimelineEntryDate
                                dateLabel={formatDate(entry.createdAt)}
                              />
                            </Box>
                            {display.body && (
                              <>
                                <Box sx={{ px: 1.5 }}>
                                  <Divider />
                                </Box>
                                <Box sx={{ p: 1.5 }}>
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {display.body}
                                  </Typography>
                                </Box>
                              </>
                            )}
                            <ActivityDomainLabel domain={domain} />
                          </Paper>
                        </TimelineContent>
                      </TimelineItem>
                    );
                  })}
                </Timeline>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>

        <Divider sx={{ my: 0.5 }} />

        {/* Contacts */}
        <Accordion
          defaultExpanded
          disableGutters
          elevation={0}
          sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent' }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={previewAccordionSummarySx}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: '100%' }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography fontWeight={700}>Contacts</Typography>
                <Chip
                  size="small"
                  label={memberRows.length}
                  sx={{
                    height: 20,
                    fontSize: 11,
                    fontWeight: 700,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: theme.palette.primary.dark,
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              </Stack>
              <Button
                size="small"
                variant="outlined"
                startIcon={<PersonAddAlt1Icon />}
                onClick={(e) => {
                  e.stopPropagation();
                  setContactFormOpen(true);
                }}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, mr: 0.5 }}
              >
                Create contact
              </Button>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            {/* Link existing contact */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="stretch"
              sx={{ mb: 1.5 }}
            >
              <Autocomplete
                size="small"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  '& .MuiOutlinedInput-root': { height: '100%', borderRadius: 2 },
                }}
                options={addableContacts}
                value={addContactValue}
                onChange={(_, value) => setAddContactValue(value)}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Stack>
                      <Typography variant="body2" fontWeight={600}>
                        {option.name || 'Unnamed contact'}
                      </Typography>
                      {(() => {
                        const primaryCompany = getContactPrimaryCompanyName(
                          option.id,
                          contactCompanies,
                        );
                        const secondary = [primaryCompany, option.email]
                          .filter(Boolean)
                          .join(' • ');
                        if (!secondary) return null;
                        return (
                          <Typography variant="caption" color="text.secondary">
                            {secondary}
                          </Typography>
                        );
                      })()}
                    </Stack>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search contacts"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <PersonAddAlt1Icon fontSize="small" sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      sx: { borderRadius: 2, bgcolor: 'background.paper' },
                    }}
                  />
                )}
              />
              <TextField
                select
                size="small"
                value={addContactRole}
                onChange={(e) => setAddContactRole(e.target.value as ContactCompanyRole)}
                sx={{
                  flexShrink: 0,
                  minWidth: 130,
                  '& .MuiOutlinedInput-root': { height: '100%' },
                  '& .MuiSelect-select': { py: 1, display: 'flex', alignItems: 'center' },
                }}
                InputProps={{ sx: { borderRadius: 2, bgcolor: 'background.paper' } }}
              >
                {ROLE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 14 }}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                size="small"
                variant="contained"
                disableElevation
                startIcon={<AddIcon />}
                onClick={() => void handleAddContact()}
                disabled={!addContactValue || isMutatingMembers}
                sx={{
                  flexShrink: 0,
                  px: 1.5,
                  fontSize: 14,
                  lineHeight: 1.25,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                Add
              </Button>
            </Stack>

            {memberRows.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                No contacts associated yet. Add one above.
              </Alert>
            ) : (
              <Stack spacing={1}>
                {memberRows.map(({ member, contact }) => {
                  const accentRole =
                    member.role === 'owner'
                      ? theme.palette.success.main
                      : member.role === 'partner'
                        ? theme.palette.warning.main
                        : theme.palette.info.main;
                  return (
                    <Paper
                      key={member.contactId}
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        p: 1.25,
                        display: 'flex',
                        gap: 1.25,
                        alignItems: 'center',
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          bgcolor: alpha(accentRole, 0.18),
                          color: accentRole,
                          fontWeight: 700,
                          fontSize: 14,
                        }}
                      >
                        {(contact?.name || '?').charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ minWidth: 0 }}
                        >
                          <Stack
                            direction="row"
                            spacing={0.375}
                            alignItems="center"
                            sx={{ minWidth: 0, flex: 1 }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              noWrap
                              sx={{ minWidth: 0 }}
                            >
                              {contact?.name || 'Unknown contact'}
                            </Typography>
                            {contact && Boolean(contact.userId?.trim()) && (
                              <Tooltip title="Has portal client account">
                                <CheckCircleIcon
                                  sx={{ color: 'success.main', fontSize: 20, flexShrink: 0 }}
                                />
                              </Tooltip>
                            )}
                          </Stack>
                          {contact && onSelectContact && (
                            <Tooltip title="Open contact">
                              <IconButton
                                size="small"
                                onClick={() => onSelectContact(contact)}
                                aria-label="Open contact"
                              >
                                <LoginIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                        {contact?.email || contact?.phone ? (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {[contact?.email, contact?.phone].filter(Boolean).join(' • ')}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Contact not found (id: {member.contactId})
                          </Typography>
                        )}
                      </Box>
                      <Select
                        size="small"
                        value={member.role}
                        onChange={(e) =>
                          void handleChangeRole(member, e.target.value as ContactCompanyRole)
                        }
                        disabled={isMutatingMembers}
                        sx={{
                          minWidth: 110,
                          '& .MuiSelect-select': { py: 0.5 },
                        }}
                      >
                        {ROLE_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <Tooltip title="Remove from company">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => void handleRemoveMember(member)}
                            disabled={isMutatingMembers}
                            aria-label="Remove contact"
                          >
                            <PersonRemoveIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            elevation: 6,
            sx: {
              mt: 0.75,
              minWidth: 240,
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
              overflow: 'hidden',
            },
          },
        }}
        MenuListProps={{ sx: { py: 0 } }}
      >
        <Box
          sx={{
            px: 1.75,
            py: 1,
            bgcolor: alpha(theme.palette.primary.main, 0.06),
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: 'uppercase',
              color: 'text.secondary',
            }}
          >
            Add activity
          </Typography>
        </Box>
        {(
          [
            { type: 'call', label: 'Log a call', subtitle: 'Phone call summary', icon: <PhoneIcon fontSize="small" />, accent: theme.palette.success.main },
            { type: 'sms', label: 'Log SMS', subtitle: 'Text message exchange', icon: <SmsIcon fontSize="small" />, accent: theme.palette.info.main },
            { type: 'email', label: 'Log an email', subtitle: 'Email correspondence', icon: <EmailIcon fontSize="small" />, accent: theme.palette.primary.main },
            { type: 'note', label: 'Create a note', subtitle: 'Internal note', icon: <NoteIcon fontSize="small" />, accent: theme.palette.warning.main },
            { type: 'shipping_repairs', label: 'Shipping & Repairs', subtitle: 'Shipping or repair update', icon: <LocalShippingIcon fontSize="small" />, accent: theme.palette.secondary.main },
          ] as const
        ).map((item) => (
          <MenuItem
            key={item.type}
            onClick={() => handlePickLogActivity(item.type as LoggableActivityType)}
            sx={{
              py: 1,
              px: 1.75,
              '&:hover': { bgcolor: alpha(item.accent, 0.08) },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 36,
                color: item.accent,
                '& .MuiSvgIcon-root': { color: item.accent },
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: alpha(item.accent, 0.14),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {item.icon}
              </Box>
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              secondary={item.subtitle}
              primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
              secondaryTypographyProps={{ fontSize: 12 }}
            />
          </MenuItem>
        ))}
        <Divider sx={{ my: 0 }} />
        <Tooltip
          title={
            hasLeadEligibleContact
              ? ''
              : 'Add a contact with a client account to this company first'
          }
          placement="left"
        >
          <span>
            <MenuItem
              disabled={!hasLeadEligibleContact}
              onClick={handlePickCreateLead}
              sx={{
                py: 1,
                px: 1.75,
                width: '100%',
                '&:hover': hasLeadEligibleContact
                  ? { bgcolor: alpha(theme.palette.primary.main, 0.08) }
                  : undefined,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 36,
                  color: hasLeadEligibleContact
                    ? theme.palette.primary.dark
                    : theme.palette.action.disabled,
                  '& .MuiSvgIcon-root': {
                    color: hasLeadEligibleContact
                      ? theme.palette.primary.dark
                      : theme.palette.action.disabled,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: alpha(
                      theme.palette.primary.main,
                      hasLeadEligibleContact ? 0.14 : 0.06,
                    ),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PostAddIcon fontSize="small" />
                </Box>
              </ListItemIcon>
              <ListItemText
                primary="Sales lead"
                secondary={
                  hasLeadEligibleContact
                    ? 'New opportunity for a contact at this company'
                    : 'Needs a contact with a client account'
                }
                primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
                secondaryTypographyProps={{ fontSize: 12 }}
              />
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip
          title={memberRows.length > 0 ? '' : 'Add a contact to this company first'}
          placement="left"
        >
          <span>
            <MenuItem
              disabled={memberRows.length === 0}
              onClick={handlePickCreateInventoryLead}
              sx={{
                py: 1,
                px: 1.75,
                width: '100%',
                '&:hover':
                  memberRows.length > 0
                    ? { bgcolor: alpha(theme.palette.primary.main, 0.08) }
                    : undefined,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 36,
                  color:
                    memberRows.length > 0
                      ? theme.palette.primary.dark
                      : theme.palette.action.disabled,
                  '& .MuiSvgIcon-root': {
                    color:
                      memberRows.length > 0
                        ? theme.palette.primary.dark
                        : theme.palette.action.disabled,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.primary.main, memberRows.length > 0 ? 0.14 : 0.06),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <LocalShippingIcon fontSize="small" />
                </Box>
              </ListItemIcon>
              <ListItemText
                primary="Inventory lead"
                secondary="Track a truck this company wants to sell"
                primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
                secondaryTypographyProps={{ fontSize: 12 }}
              />
            </MenuItem>
          </span>
        </Tooltip>
        <Divider sx={{ my: 0 }} />
        <MenuItem
          onClick={handlePickCreateTask}
          sx={{
            py: 1,
            px: 1.75,
            '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.08) },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 36,
              color: theme.palette.secondary.main,
              '& .MuiSvgIcon-root': { color: theme.palette.secondary.main },
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                bgcolor: alpha(theme.palette.secondary.main, 0.14),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <TaskIcon fontSize="small" />
            </Box>
          </ListItemIcon>
          <ListItemText
            primary="Create a task"
            secondary="Schedule a follow-up"
            primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
            secondaryTypographyProps={{ fontSize: 12 }}
          />
        </MenuItem>
      </Menu>

      <ContactCompanyActivityLogDialog
        open={logType !== null}
        type={logType}
        companyName={company.name || 'this company'}
        contacts={memberRows
          .map((row) => row.contact)
          .filter((c): c is Contact => Boolean(c))}
        isSubmitting={isLoggingContactActivity}
        onClose={() => setLogType(null)}
        onSubmit={handleLogSubmit}
      />

      <TaskFormDialog
        open={taskDialogOpen && Boolean(contactForTask)}
        onClose={() => {
          setTaskDialogOpen(false);
          setContactForTask(null);
        }}
        onSubmit={handleTaskSubmit}
        customers={taskCustomersForDialog}
        defaultCustomerId={contactForTask?.id}
        assignableUsers={assignableUsers.length > 0 ? assignableUsers : undefined}
      />

      <SelectContactForLeadDialog
        open={memberPickMode !== null}
        contacts={memberRows
          .map((row) => row.contact)
          .filter((c): c is Contact => Boolean(c))}
        companyName={company.name}
        title={
          memberPickMode === 'task'
            ? 'Who is this task for?'
            : memberPickMode === 'inventory-lead'
              ? 'Who is this inventory lead for?'
              : 'Who is this lead for?'
        }
        requirePortalAccount={memberPickMode === 'lead'}
        emptyMessage={
          memberPickMode === 'task'
            ? 'Add a contact to this company before creating a task.'
            : undefined
        }
        contactHelperText={
          memberPickMode === 'task'
            ? 'Pick the contact this task relates to.'
            : undefined
        }
        onClose={() => setMemberPickMode(null)}
        onConfirm={handleMemberPickConfirmed}
      />

      <ContactFormDialog
        open={contactFormOpen}
        onClose={() => setContactFormOpen(false)}
        linkToCompany={{ companyId: company.id, role: addContactRole }}
      />

      <TerminalFormDialog
        open={terminalCreateOpen}
        onClose={() => setTerminalCreateOpen(false)}
        defaultCompanyId={company.id}
      />

      <LeadFormDialog
        key={leadFormMountKey}
        open={leadFormOpen}
        onClose={() => {
          setLeadFormOpen(false);
          setContactForNewLead(null);
        }}
        selectedLead={null}
        isCreating
        initialClient={initialClientForNewLead}
        selectedCompany={company}
        companyMembers={companyMembersForLead}
        defaultBusinessContactId={contactForNewLead?.id ?? null}
        initialContactId={contactForNewLead?.id ?? null}
        onLeadCreated={handleLeadCreatedFromCompany}
      />

      <InventoryLeadFormDialog
        open={inventoryLeadFormOpen}
        onClose={() => {
          setInventoryLeadFormOpen(false);
          setContactForNewInventoryLead(null);
        }}
        initialContactId={contactForNewInventoryLead?.id ?? null}
        initialCompanyId={company.id}
        onLeadCreated={handleInventoryLeadCreated}
      />
    </Paper>
  );
};

export default ContactCompanyPreviewPane;
